import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import {
  capitalizeString,
  serializeClaimedReports,
  useScreenSize,
  ValidReportTypes,
} from '@ltvco/refresh-lib/utils';
import {
  mapReportTypesToReportParams,
  ReportMonitor,
  useReportRedirect,
  type RawReportType,
} from '@ltvco/refresh-lib/v1';
import {
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { ChevronRight, AddCircle } from '@mui/icons-material';
import { useState, useContext } from 'react';

import {
  DisplayTextStack,
  DisplayTextWithEllipses,
  MonitoringOverviewContainer,
  MonitoringOverviewContainerButton,
} from './Components';
import { gaCategoryMap } from './constants';
import { ManageReportMonitorsModal } from './ManageReportMonitorsModal';
import {
  SerializedReport,
  addMonitorText,
  claimMonitorText,
  containerIcon,
  containerTitleText,
  displayName,
} from './utils';

/**
 * ClaimedReportMonitorsContainer
 *
 * A container component that manages and displays report monitors for identity verification reports.
 * This component serves as the main interface for users to view and manage their claimed reports.
 *
 * Key Features:
 * - Displays claimed reports with appropriate icons and titles
 * - Provides navigation to detailed reports
 * - Manages monitor setup and configuration
 * - Handles different report types (detailed person reports vs other types)
 * - Integrates with analytics tracking
 *
 * @component
 * @example
 * <ClaimedReportMonitorsContainer
 *   reportMonitors={monitors}
 *   rawReportType="detailed_person_report"
 * />
 */

// Requires both reportMonitors and rawReportType because
//  reportMonitors can be null and we still need to populate the form correctly.
export const ClaimedReportMonitorsContainer: React.FC<{
  reportMonitors?: ReportMonitor[];
  rawReportType: RawReportType;
  reportType?: string;
}> = ({ reportMonitors, rawReportType, reportType }) => {
  const [openModal, setOpenModal] = useState(false);
  const { trackEvent } = useContext(AppConfig);

  const { isBlocked } = useSubscriptionAccessConfigContext();

  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();

  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [searchType, setSearchType] = useState('');

  const isDetailedPersonReport = rawReportType === 'detailed_person_report';
  const serializedReports: SerializedReport[] = reportMonitors
    ? serializeClaimedReports(reportMonitors, rawReportType)
    : [];
  const monitorsCount = serializedReports?.length ?? 0;
  const { isMobile, isMedium } = useScreenSize();

  const { redirectToReportUrl } = useReportRedirect();

  const navigateToReport = (permalink: string) => {
    const mappedReportType = mapReportTypesToReportParams[
      rawReportType as keyof typeof mapReportTypesToReportParams
    ] as ValidReportTypes;

    if (mappedReportType) {
      redirectToReportUrl(mappedReportType, permalink);
    } else {
      console.error('No report type found for', rawReportType);
    }
  };

  const containerButtons = () => {
    if (isDetailedPersonReport) {
      if (monitorsCount > 0) {
        return (
          <MonitoringOverviewContainerButton
            variant="outlined"
            fullWidth={isMedium}
            onClick={() => navigateToReport(serializedReports[0].permalink)}
          >
            Check Report
          </MonitoringOverviewContainerButton>
        );
      }
      return (
        <MonitoringOverviewContainerButton
          variant={'contained'}
          fullWidth={isMedium}
          endIcon={<ChevronRight />}
        >
          Set up now
        </MonitoringOverviewContainerButton>
      );
    }

    return (
      <MonitoringOverviewContainerButton
        variant={monitorsCount > 0 ? 'outlined' : 'contained'}
        fullWidth={isMedium}
        endIcon={<AddCircle />}
      >
        {monitorsCount > 0
          ? addMonitorText(rawReportType)
          : claimMonitorText(rawReportType)}
      </MonitoringOverviewContainerButton>
    );
  };

  const theme = useTheme();

  const reportName = () => {
    if (isDetailedPersonReport) {
      return `${capitalizeString(serializedReports[0].name || '')}, ${
        serializedReports[0].location
      }`;
    }

    return displayName[rawReportType](serializedReports[0]);
  };

  const displayFirstReportName = () => {
    if (monitorsCount > 1 && !isDetailedPersonReport) {
      return (
        <>
          <DisplayTextWithEllipses>
            {displayName[rawReportType](serializedReports[0])}
          </DisplayTextWithEllipses>
          <Text
            sx={{
              color: theme.palette.primary.light,
              whiteSpace: 'nowrap',
            }}
            ml={1}
            variant="body1"
          >
            (+{monitorsCount - 1} more)
          </Text>
        </>
      );
    }
    return (
      <>
        <DisplayTextWithEllipses
          sx={{
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {monitorsCount > 0 ? reportName() : 'Not claimed'}
        </DisplayTextWithEllipses>
        <Text whiteSpace="pre"> </Text>
      </>
    );
  };

  return (
    <>
      <MonitoringOverviewContainer
        direction="column"
        onClick={() => {
          const gaCategory =
            gaCategoryMap[rawReportType as keyof typeof gaCategoryMap];

          trackEvent(gaCategory, 'navigation click', 'id monitor settings');

          if (isBlocked(reportType || '')) {
            setSearchType(capitalizeString(reportType || ''));
            openFeatureBlockedModal();
            return;
          }

          setOpenModal(true);
        }}
      >
        <Stack direction="row" justifyContent="space-between" mb={[2, 0]}>
          <Stack direction="row">
            <img
              src={containerIcon[rawReportType]}
              width="36px"
              height="39px"
              alt="name-monitoring-icon"
            />
            <Stack direction="column" ml={2} sx={{}}>
              <Text variant="h6" sx={{ whiteSpace: 'pre-wrap' }} mb={1}>
                {containerTitleText(rawReportType, !isMobile)}
              </Text>
            </Stack>
          </Stack>
        </Stack>

        <DisplayTextStack direction="row" ml={6.5}>
          {displayFirstReportName()}
        </DisplayTextStack>

        {containerButtons()}
      </MonitoringOverviewContainer>
      <ManageReportMonitorsModal
        serializedReports={serializedReports}
        rawReportType={rawReportType}
        openModal={openModal}
        setOpenModal={setOpenModal}
        navigateToReport={navigateToReport}
      />
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </>
  );
};
